import type { AccordionBlock } from "./AccordionSection";

type Props = {
  blocks: AccordionBlock[];
  eyebrow?: string;
  title?: string;
  intro?: string;
};

export function ComparisonModulesGrid({
  blocks,
  eyebrow = "13 Vergleichsmodule",
  title = "Was wir konkret für Sie vergleichen",
  intro = "Jede Karte zeigt klar: Was wir prüfen, was Sie davon haben und ein Beispiel aus der Praxis. Die Chips unten sind die einzelnen Kriterien, die in den Vergleich einfließen.",
}: Props) {
  return (
    <section className="border-b border-[var(--rule)] surface-cloud">
      <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-14 md:py-20">
        <div className="max-w-3xl mb-10 md:mb-12">
          <div className="eyebrow text-[var(--leaf)] mb-3">{eyebrow}</div>
          <h2 className="font-display font-bold text-navy text-2xl md:text-4xl leading-tight">
            {title}
          </h2>
          <p className="mt-4 text-navy/75 text-base leading-relaxed">{intro}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {blocks.map((b, i) => (
            <article
              key={b.id}
              className="bg-white rounded-2xl border border-[var(--rule)] shadow-sm hover:shadow-md transition p-6 md:p-7 flex flex-col"
            >
              {/* Header */}
              <div className="flex items-start gap-4 pb-4 border-b border-[var(--rule)]">
                <span className="shrink-0 w-11 h-11 rounded-xl bg-[color-mix(in_oklab,var(--leaf)_14%,white)] text-[var(--leaf-deep)] font-display font-bold text-base inline-flex items-center justify-center">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display font-bold text-navy text-lg md:text-xl leading-snug pt-1.5">
                  {b.title}
                </h3>
              </div>

              {/* Three rows: Was wir prüfen / Ihr Nutzen / Beispiel */}
              <div className="mt-5 space-y-4">
                <Row
                  label="Was wir prüfen"
                  color="bg-navy text-white"
                  text={b.explanation}
                />
                <Row
                  label="Ihr Nutzen"
                  color="bg-[var(--leaf-deep)] text-white"
                  text={b.benefit}
                />
                <Row
                  label="Beispiel"
                  color="bg-orange text-white"
                  text={b.example}
                />
              </div>

              {/* Criteria chips */}
              {b.bullets && b.bullets.length > 0 && (
                <div className="mt-5 pt-4 border-t border-[var(--rule)]">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-navy/55 mb-2.5">
                    Geprüfte Kriterien
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {b.bullets.map((p) => (
                      <span
                        key={p}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-navy/85 bg-[#f4f6fa] border border-[var(--rule)] rounded-full px-3 py-1"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--leaf-deep)]" />
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Row({ label, color, text }: { label: string; color: string; text: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
      <span
        className={`shrink-0 inline-flex items-center justify-center text-[11px] font-semibold uppercase tracking-wide rounded-md px-2.5 py-1 ${color} w-fit sm:w-[120px]`}
      >
        {label}
      </span>
      <p className="text-sm leading-relaxed text-navy/80">{text}</p>
    </div>
  );
}
